import { useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { nomCategorie } from '../../analysis/donnees'
import { comparerAnnees, parCategorie, serieMensuelle, type Mesure } from '../../analysis/statistiques'
import { formatMontant, formatPourcentage, somme } from '../../domain/argent'
import { anneeDe, aujourdhui, formatMoisCourt } from '../../domain/temps'
import { Bascule, Carte, Entete, IconeChevron, Montant, Pastille, SelectCompte } from '../composants'
import { useDonnees } from '../donnees'
import { BarresGroupees, BarresHorizontales, Figure, Legende } from '../Graphiques'

export function PageStatistiques() {
  const { d, ix } = useDonnees()
  const [params, setParams] = useSearchParams()
  const jour = aujourdhui()
  const annee = Number(params.get('annee') ?? anneeDe(jour))
  const compteId = params.get('compte')
  const mesure: Mesure = params.get('mesure') === 'flux' ? 'flux' : 'charge'

  const maj = (cle: string, v: string | null) => {
    const p = new URLSearchParams(params)
    if (v) p.set(cle, v)
    else p.delete(cle)
    setParams(p, { replace: true })
  }

  const annees = useMemo(() => {
    const s = new Set(d.operations.map((o) => anneeDe(o.date)))
    s.add(anneeDe(jour))
    return [...s].sort((a, b) => b - a)
  }, [d.operations, jour])

  const serie = useMemo(() => serieMensuelle(ix, annee, compteId, mesure), [ix, annee, compteId, mesure])
  const categories = useMemo(
    () => parCategorie(ix, { du: `${annee}-01-01`, au: `${annee}-12-31`, compteId }, 'depense', mesure),
    [ix, annee, compteId, mesure],
  )
  const comparaison = useMemo(() => comparerAnnees(ix, annee, jour, compteId), [ix, annee, jour, compteId])

  const totalDepenses = somme(serie.map((p) => p.depenses))
  const totalRevenus = somme(serie.map((p) => p.revenus))
  const moisEcoules = annee === anneeDe(jour) ? Number(jour.slice(5, 7)) : 12
  const couleurs = ['var(--viz-depense)', 'var(--viz-revenu)']

  return (
    <>
      <Entete titre="Statistiques" sous={String(annee)} />
      <main className="contenu">
        <div className="filtres">
          <select value={annee} onChange={(e) => maj('annee', e.target.value)} aria-label="Année">
            {annees.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
          <SelectCompte comptes={d.comptes} valeur={compteId} onChange={(c) => maj('compte', c)} tous="Tous les comptes" />
          <Bascule
            etiquette="Mesure"
            valeur={mesure}
            onChange={(m) => maj('mesure', m === 'flux' ? 'flux' : null)}
            options={[
              { valeur: 'charge', libelle: 'À ma charge' },
              { valeur: 'flux', libelle: 'Montants payés' },
            ]}
          />
        </div>

        <div className="mesures">
          <div className="mesure">
            <div className="mesure__valeur"><Montant valeur={totalDepenses} /></div>
            <div className="mesure__libelle">dépenses</div>
          </div>
          <div className="mesure">
            <div className="mesure__valeur"><Montant valeur={Math.round(totalDepenses / Math.max(1, moisEcoules))} /></div>
            <div className="mesure__libelle">par mois en moyenne</div>
          </div>
          <div className="mesure">
            <div className="mesure__valeur"><Montant valeur={totalRevenus - totalDepenses} /></div>
            <div className="mesure__libelle">épargne</div>
          </div>
        </div>

        <Carte>
          <Figure
            titre="Par mois"
            legende={<Legende series={[{ libelle: 'Dépenses', couleur: couleurs[0]! }, { libelle: 'Revenus', couleur: couleurs[1]! }]} />}
            tableau={
              <table>
                <thead>
                  <tr><th>Mois</th><th className="nombre">Dépenses</th><th className="nombre">Revenus</th><th className="nombre">Solde</th></tr>
                </thead>
                <tbody>
                  {serie.map((p) => (
                    <tr key={p.mois}>
                      <td>{formatMoisCourt(p.mois)}</td>
                      <td className="nombre">{formatMontant(p.depenses)}</td>
                      <td className="nombre">{formatMontant(p.revenus)}</td>
                      <td className="nombre">{formatMontant(p.solde)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            }
          >
            <BarresGroupees
              etiquette={`Dépenses et revenus par mois, ${annee}`}
              couleurs={couleurs}
              groupes={serie.map((p) => ({ libelle: formatMoisCourt(p.mois).slice(0, 3), valeurs: [p.depenses, p.revenus] }))}
            />
          </Figure>
        </Carte>

        <Carte>
          <Figure
            titre="Par catégorie"
            tableau={
              <table>
                <thead>
                  <tr><th>Catégorie</th><th className="nombre">Montant</th><th className="nombre">Part</th><th className="nombre">Opérations</th></tr>
                </thead>
                <tbody>
                  {categories.map((c) => (
                    <tr key={c.categorieId}>
                      <td>{nomCategorie(ix, c.categorieId)}</td>
                      <td className="nombre">{formatMontant(c.montant)}</td>
                      <td className="nombre">{formatPourcentage(Math.round(c.part * 1000) / 10)}</td>
                      <td className="nombre">{c.nbOperations}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            }
          >
            {categories.length === 0 ? (
              <p className="tres-doux">Aucune dépense.</p>
            ) : (
              <BarresHorizontales
                lignes={categories.map((c) => ({
                  cle: c.categorieId,
                  libelle: nomCategorie(ix, c.categorieId),
                  valeur: c.montant,
                  detail: formatPourcentage(Math.round(c.part * 100)),
                  couleur: ix.categorieParId.get(c.categorieId)?.couleur,
                }))}
              />
            )}
          </Figure>
        </Carte>

        <Carte titre="Bilan annuel par catégorie">
          <p className="tres-doux" style={{ marginBottom: 8 }}>
            Ce que chaque poste vous a réellement coûté, après remboursements et parts refacturées.
          </p>
          <ul className="liste">
            {categories.map((c) => (
              <li key={c.categorieId}>
                <Link to={`/bilan/${c.categorieId}?annee=${annee}`} className="item">
                  <Pastille couleur={ix.categorieParId.get(c.categorieId)?.couleur ?? '#999'} />
                  <span className="grandir">{nomCategorie(ix, c.categorieId)}</span>
                  <span className="chevron"><IconeChevron /></span>
                </Link>
              </li>
            ))}
          </ul>
        </Carte>

        {comparaison.some((c) => c.precedent > 0) && (
          <Carte titre={`${annee} face à ${annee - 1}`}>
            <p className="tres-doux" style={{ marginBottom: 8 }}>
              {annee === anneeDe(jour) ? `Sur la même période (1er janvier – ${jour.slice(8)}.${jour.slice(5, 7)}), à votre charge.` : 'Années complètes, à votre charge.'}
            </p>
            <div className="defilable">
              <table>
                <thead>
                  <tr><th>Catégorie</th><th className="nombre">{annee - 1}</th><th className="nombre">{annee}</th><th className="nombre">Écart</th></tr>
                </thead>
                <tbody>
                  {comparaison.map((c) => (
                    <tr key={c.categorieId}>
                      <td>{nomCategorie(ix, c.categorieId)}</td>
                      <td className="nombre">{formatMontant(c.precedent)}</td>
                      <td className="nombre">{formatMontant(c.actuel)}</td>
                      <td className="nombre">
                        {c.variation === null ? 'nouveau' : `${c.variation > 0 ? '+' : c.variation < 0 ? '−' : ''}${formatPourcentage(Math.abs(Math.round(c.variation)))}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Carte>
        )}
      </main>
    </>
  )
}
